import React from "react";
import { Loader2, AlertTriangle } from "lucide-react";

const STATUS_COLORS = {
  "not started": { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
  "in-progress": { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6" },
  "in progress": { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6" },
  "complete": { bg: "#ecfdf5", text: "#047857", dot: "#10b981" },
  "completed": { bg: "#ecfdf5", text: "#047857", dot: "#10b981" },
  "done": { bg: "#ecfdf5", text: "#047857", dot: "#10b981" },
  "on hold": { bg: "#fff7ed", text: "#c2410c", dot: "#f97316" },
  "deferred": { bg: "#f5f3ff", text: "#6d28d9", dot: "#8b5cf6" },
  "cancelled": { bg: "#fef2f2", text: "#b91c1c", dot: "#ef4444" },
};
const PRIORITY_COLORS = {
  "high": { bg: "#fef2f2", text: "#b91c1c" },
  "medium": { bg: "#fffbeb", text: "#b45309" },
  "low": { bg: "#f0fdf4", text: "#15803d" },
};
const HEALTH_COLORS = {
  "On Track": { bg: "#ecfdf5", text: "#047857" },
  "At Risk": { bg: "#fffbeb", text: "#b45309" },
  "Blocked": { bg: "#fef2f2", text: "#b91c1c" },
};

export const statusColor = (s) => STATUS_COLORS[String(s || "").toLowerCase()] || { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" };
export const priorityColor = (s) => PRIORITY_COLORS[String(s || "").toLowerCase()] || { bg: "#f1f5f9", text: "#475569" };

export function StatusPill({ value }) {
  const c = statusColor(value);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c.dot }} />{value || "—"}
    </span>
  );
}
export function PriorityPill({ value }) {
  const c = priorityColor(value);
  return <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }}>{value || "—"}</span>;
}
export function HealthPill({ value }) {
  const c = HEALTH_COLORS[value] || HEALTH_COLORS["On Track"];
  return <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: c.bg, color: c.text }}>{value}</span>;
}

// avatar = { name, initials, color }
export function Avatar({ avatar, size = 32 }) {
  if (!avatar) return (
    <div className="flex items-center justify-center rounded-full bg-slate-200 font-semibold text-slate-500 ring-2 ring-white" style={{ width: size, height: size, fontSize: size * 0.36 }}>–</div>
  );
  return (
    <div className="flex items-center justify-center rounded-full font-semibold text-white ring-2 ring-white"
      style={{ backgroundColor: avatar.color, width: size, height: size, fontSize: size * 0.36 }} title={avatar.name}>
      {avatar.initials}
    </div>
  );
}

export function ProgressBar({ value, color = "#D0333A" }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Loading({ label = "Loading live data..." }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-slate-400">
      <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50"><AlertTriangle className="h-6 w-6 text-red-500" /></div>
      <p className="max-w-md text-sm font-medium text-slate-600">{message}</p>
      {onRetry && <button onClick={onRetry} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">Retry</button>}
    </div>
  );
}

export const currency = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);

export const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
